import React, { useState } from "react";
import { Download, Check, Triangle } from "lucide-react";
import Reveal from "./Reveal";
import { RECIPE_BULLETS, IMAGES, APPLY_URL, FORGED_PLATE_PDF, FORGED_PLATE_FILENAME } from "../../data/mock";
import Embers from "./Embers";

export default function ForgedPlate() {
  const [form, setForm] = useState({ first: "", email: "", phone: "" });
  const [sent, setSent] = useState(false);

  const triggerDownload = async () => {
    // Best-effort: fetch the file and download with a proper filename.
    // Fallback: open in a new tab so the user can save it manually.
    try {
      const res = await fetch(FORGED_PLATE_PDF, { mode: "cors" });
      if (!res.ok) throw new Error("bad response");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = FORGED_PLATE_FILENAME;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      const a = document.createElement("a");
      a.href = FORGED_PLATE_PDF;
      a.target = "_blank";
      a.rel = "noreferrer";
      a.download = FORGED_PLATE_FILENAME;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.first || !form.email) return;
    try { localStorage.setItem("forgedPlateLead", JSON.stringify({ ...form, at: new Date().toISOString() })); } catch {}
    setSent(true);
    triggerDownload();
  };

  return (
    <section id="forged-plate" className="relative py-28 md:py-40 border-b hairline overflow-hidden">
      <div className="absolute inset-0 opacity-40">
        <img src={IMAGES.forgeHammer} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,9,8,0.9) 0%, rgba(10,9,8,0.85) 60%, rgba(10,9,8,1) 100%)" }}></div>
        <Embers density={40} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-[1.1fr_1fr] gap-14 md:gap-20 items-center">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ember-3)] mb-6">Free Resource</div>
          <Reveal>
            <h2 className="font-display text-[54px] md:text-[92px] leading-[0.96] tracking-[-0.03em]">
              The Forged<br /> <span className="italic-serif text-[var(--ember-3)]">Plate</span> Recipes
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-8 max-w-[540px] text-[var(--text-muted)] text-[17px] leading-[1.75]">
              Thirty meals built for men who train, lead, and want to make it to seventy. Heavy on protein. Rich in gut-supportive foods. Written for those who steward the temple.
            </p>
          </Reveal>
          <ul className="mt-10 space-y-4">
            {RECIPE_BULLETS.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-[15px] text-[var(--text)]">
                <Triangle size={12} fill="currentColor" className="text-[var(--ember-2)] mt-1.5 rotate-90" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Lead card */}
        <div className="relative">
          <div className="absolute -inset-6 border hairline-2 pointer-events-none"></div>
          <div className="relative bg-[rgba(20,18,16,0.85)] backdrop-blur-md border hairline-2 p-8 md:p-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display text-[28px] leading-none">27:17</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--text-muted)] mt-1">Performance</div>
              </div>
              <div className="text-right">
                <div className="font-display italic text-[22px] text-[var(--ember-3)]">The Forged Plate</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">Recipes</div>
              </div>
            </div>
            <div className="h-px w-full bg-[var(--line-2)] my-8"></div>

            {!sent ? (
              <>
                <div className="font-display text-[26px] leading-[1.1]">Get the free guide</div>
                <p className="text-[var(--text-muted)] text-[14px] mt-2">Enter your info and we'll send you straight to the download.</p>
                <form onSubmit={submit} className="mt-8 space-y-4">
                  <Field label="First Name" value={form.first} onChange={(v) => setForm({ ...form, first: v })} />
                  <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                  <Field label="Phone Number" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                  <button type="submit" className="btn-ember w-full justify-center mt-4">→ Send Me the Guide</button>
                  <p className="text-center font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-dim)] mt-3">No spam. No fluff. Unsubscribe anytime.</p>
                </form>
              </>
            ) : (
              <div className="py-6 flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-[var(--ember-2)] flex items-center justify-center"><Check className="text-[var(--bg)]"/></div>
                <div className="font-display text-[26px] mt-6">Your Forged Plate is ready.</div>
                <p className="text-[var(--text-muted)] mt-3 max-w-[340px] text-[14px] leading-[1.7]">If the download didn't start automatically, tap below. We also saved your info so we can send follow-up notes.</p>
                <button type="button" onClick={triggerDownload} className="btn-ember mt-6"><Download size={14}/> Download the PDF</button>
                <a href={APPLY_URL} target="_blank" rel="noreferrer" className="btn-ghost mt-3">Begin the Application</a>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-dim)] mt-6">2717_Forged_Plate_V2.pdf</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, type = "text", value, onChange }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-transparent border-b hairline-2 focus:border-[var(--ember-2)] outline-none py-3 text-[15px] text-[var(--text)] transition-colors"
      />
    </label>
  );
}
